import { Children, forwardRef } from "react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface ListProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Vertical list with hairline dividers between rows. */
export const List = forwardRef<HTMLDivElement, ListProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="list"
        className={cn("w-full divide-y divide-border-default", className)}
        {...props}
      >
        {Children.map(children, (child) => (
          <div role="listitem">{child}</div>
        ))}
      </div>
    );
  },
);
List.displayName = "List";

export interface ListItemProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Single non-interactive row. */
export const ListItem = forwardRef<HTMLDivElement, ListItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex min-h-14 items-center gap-3 px-4 py-3", className)}
        {...props}
      />
    );
  },
);
ListItem.displayName = "ListItem";

export interface ListItemButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

/** Full-width interactive row — same layout as ListItem with hover/active states. */
export const ListItemButton = forwardRef<
  HTMLButtonElement,
  ListItemButtonProps
>(({ className, ...props }, ref) => {
  return (
    <button
      type="button"
      ref={ref}
      className={cn(
        "flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left transition-colors outline-none hover:bg-surface-raised active:bg-surface-raised focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-inset",
        className,
      )}
      {...props}
    />
  );
});
ListItemButton.displayName = "ListItemButton";

export interface ListItemIconProps
  extends React.HTMLAttributes<HTMLSpanElement> {}

/** Fixed 24px leading slot for row icons. */
export const ListItemIcon = forwardRef<HTMLSpanElement, ListItemIconProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn("flex w-6 shrink-0", className)}
        {...props}
      />
    );
  },
);
ListItemIcon.displayName = "ListItemIcon";

export interface ListItemTextProps {
  /** Primary line — body weight, full-emphasis text. */
  primary?: ReactNode;
  /** Optional secondary line — caption, muted. */
  secondary?: ReactNode;
  className?: string;
}

/** Stacked primary/secondary text filling the remaining row width. */
export const ListItemText = forwardRef<HTMLDivElement, ListItemTextProps>(
  ({ primary, secondary, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex min-w-0 flex-1 flex-col gap-0.5", className)}
      >
        {primary ? (
          <span className="type-body-md text-content-primary">{primary}</span>
        ) : null}
        {secondary ? (
          <span className="type-caption text-content-secondary">
            {secondary}
          </span>
        ) : null}
      </div>
    );
  },
);
ListItemText.displayName = "ListItemText";

export interface ListSectionHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Small leading icon (16px, content-secondary) — e.g. a site/plot glyph. */
  icon?: ReactNode;
  /** Uppercase caption title. */
  title: ReactNode;
  /** Rendered as a muted trailing "(n)". */
  count?: number;
  /** Trailing action (button/toggle) aligned end. */
  action?: ReactNode;
}

/**
 * In-card sub-heading band for grouped lists — the second layer of the
 * hierarchy (page SectionHeader → ListSectionHeader → rows). Tinted
 * background, uppercase caption, sits flush at the top of a ListGroup/Card
 * with rows divided beneath it.
 */
export const ListSectionHeader = forwardRef<
  HTMLDivElement,
  ListSectionHeaderProps
>(({ icon, title, count, action, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 bg-surface-raised px-4 py-2.5 text-content-secondary",
        className,
      )}
      {...props}
    >
      {icon ? (
        <span className="flex size-4 shrink-0 items-center [&_svg]:size-4">
          {icon}
        </span>
      ) : null}
      <span className="type-caption font-semibold uppercase leading-4 tracking-wide">
        {title}
      </span>
      {typeof count === "number" ? (
        <span className="type-caption font-normal leading-4 tracking-normal text-content-secondary">
          ({count})
        </span>
      ) : null}
      {action ? (
        <span className="ml-auto flex items-center">{action}</span>
      ) : null}
    </div>
  );
});
ListSectionHeader.displayName = "ListSectionHeader";
